/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import {
    type Command, type PromptConfigFull, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract,
    UNPLACEHOLDER, UtilService,
} from '@dm/core';
import { ToolService } from 'app/common/services';
import { CIFSShare } from 'app/common/types/mo';
import { SWITCH } from 'app/config/switch';

import { type CIFSShareVm } from '../../share';
import { CifsShareStrService, OffLineFileModeE } from '../cifs-share-str.service';
import { ShareTypeE } from './../../share.enum';
import { CifsShareService } from './../cifs-share.service';

@Component({
    selector: 'app-modify-cifs-share',
    templateUrl: './modify-cifs-share.component.html',
    styleUrls: ['./modify-cifs-share.component.scss'],
})
export class ModifyCifsShareComponent extends FormSlideWindowAbstract implements OnInit {
    @Input() data: CIFSShare;

    i18n = this.str.i18n;
    closeArgs = { refresh: false };
    UNPLACEHOLDER = UNPLACEHOLDER;
    ShareTypeE = ShareTypeE;
    OffLineFileModeE = OffLineFileModeE;
    SWITCH = SWITCH;

    vm: FormGroup;
    errorMessage: any;

    get vmValue(): CIFSShareVm {
        return this.vm ? this.vm.value : null;
    }

    constructor(
        injector: Injector,
        public str: CifsShareStrService,
        private toolService: ToolService,
        private CIFSService: CifsShareService,
        private utilService: UtilService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.pageReady = true;
        this.windowVisible = true;
    }

    initVm(): void {
        this.vm = new FormGroup({
            DESCRIPTION: new FormControl(this.utilService.decodeHTML(this.data.DESCRIPTION), [
                DMValidators.rangeByteLength(0, 255),
            ]),

            // notify
            ENABLENOTIFY: new FormControl(this.data.ENABLENOTIFY === 'true'),
            ENABLECA: new FormControl(this.data.ENABLECA === 'true'),
            smb3EncryptionEnable: new FormControl(this.data.smb3EncryptionEnable === 'true'),
            unencryptedAccess: new FormControl(this.data.unencryptedAccess === 'true'),
            ABEENABLE: new FormControl(this.data.ABEENABLE === 'true'),
            ENABLESHOWSNAPSHOT: new FormControl(this.data.ENABLESHOWSNAPSHOT === 'true'),
            ENABLESHOWPREVIOUSVERSIONS: new FormControl(this.data.ENABLESHOWPREVIOUSVERSIONS === 'true'),
            OFFLINEFILEMODE: new FormControl(this.data.OFFLINEFILEMODE),
        }, { updateOn: 'blur' });

        this.errorMessage = {
            DESCRIPTION: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 255),
            },
        };
    }

    buildCmds(): Command[] {
        const cmds: Command[] = [this.buildCIFSCmd()];
        return cmds;
    }

    /**
     * 构建修改共享的rest
     */
    buildCIFSCmd(): Command {
        const vmValue = this.vmValue;
        const params: CIFSShare = {
            DESCRIPTION: vmValue.DESCRIPTION,
            ENABLENOTIFY: vmValue.ENABLENOTIFY,
            ENABLECA: vmValue.ENABLECA,
            ABEENABLE: vmValue.ABEENABLE,
            ENABLESHOWSNAPSHOT: vmValue.ENABLESHOWSNAPSHOT,
            smb3EncryptionEnable: vmValue.smb3EncryptionEnable,
            unencryptedAccess: vmValue.unencryptedAccess,
            ENABLESHOWPREVIOUSVERSIONS: vmValue.ENABLESHOWPREVIOUSVERSIONS,
            OFFLINEFILEMODE: vmValue.OFFLINEFILEMODE,
        };

        if (this.data.vstoreId) {
            params.vstoreId = this.data.vstoreId;
        }

        return {
            name: this.data.subType === ShareTypeE.Homedir ? this.i18n.rest.homedir.modify : this.i18n.rest.modifyShare,
            nameParams: [this.data.NAME],
            action: this.CIFSService.modify(this.data.ID, params),
        };
    }

    prompt(): PromptConfigFull[] {
        const prompts: PromptConfigFull[] = [];
        const title = this.data.subType === ShareTypeE.Homedir ? this.i18n.title.modifyHomedirTitle : this.i18n.title.modifyShare;
        // 开启SMB3加密开关高危提示
        if (this.vm.changedValue.smb3EncryptionEnable) {
            prompts.push({
                id: 'prompt-modify-cifs-share-SMB3EncryptEnabledTip',
                opType: 'warning',
                title,
                content: this.i18n.tip.SMB3EncryptEnabledTip,
            });
        }
        // 开启非加密客户端访问高危提示
        if (this.vm.changedValue.unencryptedAccess) {
            prompts.push({
                id: 'prompt-modify-cifs-share-enableUnencryptedAccessTip',
                opType: 'warning',
                title,
                content: this.i18n.tip.enableUnencryptedAccessTip,
            });
        }
        //离线文件缓存模式值不是“关闭” 高危提示
        if (this.vm.changedValue.OFFLINEFILEMODE && this.vm.changedValue.OFFLINEFILEMODE !== OffLineFileModeE.None) {
            prompts.push({
                id: 'prompt-cifs-share-modify-modeNotNoneTip',
                opType: 'info',
                title,
                content: this.i18n.tip.modeNotNoneTip,
            });
        }
        //开启CA高危提示
        if (this.vm.changedValue.ENABLECA) {
            prompts.push({
                id: 'prompt-cifs-share-modify-enableCaTip',
                opType: 'info',
                title,
                content: this.i18n.tip.enableCaTip,
            });
        }

        return prompts;
    }
}
